/**
 * The ONE operation — "never destroy, always create".
 *
 * Every structural change to a recording's clips is the same shape:
 *   N active records go in  →  they become OBSOLETE (stamps kept, audio cache
 *   dropped)  →  M new records come out, each with a fresh name (A1.7, A1.8…)
 *   and a made-from list pointing at the inputs.
 *
 * Cut, merge, partial merge, word transfer, "remove" (→ becomes a silence
 * record over the same span) are all this. The invariant enforced here is the
 * app's oldest promise: the ACTIVE records of a task tile its audio exactly —
 * every millisecond labeled, nothing duplicated, nothing blank.
 *
 * There is only one audio file. Records are timestamps + word stamps into it;
 * the per-clip WAVs written for the new active records are a playback cache,
 * not files of record — obsolete records live on without any audio at all.
 */

import { ensureTaskCode, getSegmentsByTask, newId, reconcileTask, reviseSegments } from '../db';
import { isActiveLeaf, type Segment, type SegmentKind, type TaskStatus, type Word } from '../types';
import { round3 } from '../lib/num';
import { sliceToWav } from './audioSlicer';

/** What a recut produces, before ids/names/indices are assigned. */
export interface NewRecordSpec {
  startSec: number;
  endSec: number;
  words: Word[];
  kind: SegmentKind;
  confidence: number;
  /** Human placed this boundary (walls, ripple stops). Default true — recuts are human acts. */
  placedBy?: 'human';
}

export interface RecutResult {
  created: Segment[];
  obsoleted: Segment[];
  status?: TaskStatus;
}

/** heard/contextual stay derived from the word stamps. */
export function labelOf(words: Word[], kind: SegmentKind): string {
  const text = words.map((w) => w.text).join(' ').trim();
  if (text) return text;
  return kind === 'silence' ? 'silence' : kind === 'noise' ? 'noise' : 'unclear';
}

/**
 * The word entities of a record.
 *
 * READ THE CAVEAT. Rows produced by the aligned pipeline carry `words` with
 * stable ids, and those are returned untouched. Rows that predate word stamps —
 * everything the LEGACY pipeline has ever produced, which is every task made on
 * a phone — have none, so ids are MINTED FRESH ON EVERY CALL. "A word keeps its
 * id" therefore holds only within a single service call, and only for
 * aligned-path rows. Nothing may persist an id derived this way and expect to
 * recognise it later.
 *
 * (The old docstring claimed "derived once, stable after". Nothing writes the
 * derived list back, so it never was.)
 */
export function wordsOf(seg: Segment): Word[] {
  if (seg.words && seg.words.length) return seg.words;
  const text = (seg.correctedText ?? seg.heard).trim();
  if (!text || seg.kind === 'silence' || text === 'silence' || text === 'unclear' || text === 'noise') return [];
  return text.split(/\s+/).filter(Boolean).map((t) => ({ id: newId(), text: t }));
}

/**
 * Version names, per the user's rule:
 *  - a JOIN (or any flat recut) mints the next TOP-LEVEL number: A1.1+A1.2 -> A1.3
 *  - a SPLIT nests under its parent: A1.1 -> A1.1.1, A1.1.2 (pass `nameBase`)
 * Numbers are never reused; obsolete names stay in the record forever.
 */
async function nextNames(taskId: string, count: number, nameBase?: string): Promise<string[]> {
  if (nameBase) return Array.from({ length: count }, (_, i) => `${nameBase}.${i + 1}`);
  // Minting a code for a task that predates the naming rule is `db`'s job — it
  // writes the task row. This function used to hold a second copy of it, and
  // the aligned pipeline a third.
  const code = await ensureTaskCode(taskId);
  const rows = await getSegmentsByTask(taskId);
  let maxSeq = 0;
  for (const s of rows) {
    // top-level sequence = the SECOND dotted component (A1.7 and A1.7.2 both say 7)
    const m = s.name?.match(/^[^.]+\.(\d+)/);
    if (m) maxSeq = Math.max(maxSeq, Number(m[1]));
  }
  if (maxSeq === 0) maxSeq = rows.length; // legacy rows count as their positions
  return Array.from({ length: count }, (_, i) => `${code ?? 'A?'}.${maxSeq + 1 + i}`);
}

/**
 * THE PARTITION CHECK for a recut.
 *
 * THE PARTITION INVARIANT — every millisecond of a recording is labeled
 * exactly once. FOUR places in the app keep it, and they do two different
 * jobs:
 *   CHECK    `recut.assertTiles`          — throws when a recut does not tile
 *            `WaveLane.validateMoved`     — refuses an illegal drag
 *   CONSTRUCT `pipeline/alignedTiling.tileFromWords` — seals the seams as it
 *                                           builds the timeline
 *            `boundaryEdits.moveBoundary` — moving an edge TRANSFERS audio, so
 *                                           nothing is ever left unlabeled
 *
 * This is one of the two CHECKS.
 *
 * `outputs` must tile `[spanStart, spanEnd]` exactly: no gap, no overlap, both
 * ends flush, within `eps`. Returns the outputs sorted by start with their
 * boundaries SNAPPED so adjacent records share a boundary VALUE — approximate
 * agreement within eps is not enough, because every later comparison is `===`.
 *
 * MIGRATION SEAM: this is the single-function swap point for
 * `src/model/ids.ts`'s `checkTiling`. That function asks the same question of
 * the proposal/consensus model's nodes; when `ids.ts` is wired, this body is
 * what it replaces. Keep it a pure function of (span, outputs) so the swap
 * stays a one-line change — see `src/model/README.md`.
 */
function assertTiles(
  spanStart: number,
  spanEnd: number,
  outputs: NewRecordSpec[],
): NewRecordSpec[] {
  const sorted = [...outputs].sort((a, b) => a.startSec - b.startSec);
  if (sorted.length) {
    const eps = 0.01;
    if (Math.abs(sorted[0].startSec - spanStart) > eps || Math.abs(sorted[sorted.length - 1].endSec - spanEnd) > eps) {
      throw new Error('The new records do not cover the exact span of the old ones.');
    }
    for (let i = 1; i < sorted.length; i++) {
      if (Math.abs(sorted[i].startSec - sorted[i - 1].endSec) > eps) {
        throw new Error('The new records leave a gap or overlap — every millisecond must stay labeled.');
      }
    }
    // snap exactly: outputs share boundary VALUES, no float drift
    sorted[0].startSec = spanStart;
    sorted[sorted.length - 1].endSec = spanEnd;
    for (let i = 1; i < sorted.length; i++) sorted[i].startSec = sorted[i - 1].endSec;
  } else {
    throw new Error('A recut must produce at least one record (use a silence record, never a hole).');
  }
  return sorted;
}

/**
 * Apply one recut. `inputs` must be ACTIVE records of the task; `outputs`
 * must tile the union of the inputs' spans exactly (small float slack).
 */
export async function applyRecut(
  taskId: string,
  buffer: AudioBuffer,
  inputs: Segment[],
  outputs: NewRecordSpec[],
  options: { nameBase?: string } = {},
): Promise<RecutResult> {
  if (inputs.length === 0) throw new Error('A recut needs at least one input record.');
  for (const s of inputs) {
    if (s.obsoleteAt) throw new Error(`“${s.heard}” is already obsolete.`);
    if (s.taskId !== taskId) throw new Error('All inputs must belong to the same recording.');
  }
  const spanStart = Math.min(...inputs.map((s) => s.startSec));
  const spanEnd = Math.max(...inputs.map((s) => s.endSec));

  const sorted = assertTiles(spanStart, spanEnd, outputs);
  const names = await nextNames(taskId, sorted.length, options.nameBase);
  const madeFrom = inputs.map((s) => s.name ?? s.id);
  const created: Segment[] = sorted.map((o, i) => {
    const words = o.words;
    const label = labelOf(words, o.kind);
    return {
      id: newId(),
      taskId,
      index: -1, // renumbered below
      startSec: round3(o.startSec),
      endSec: round3(o.endSec),
      heard: label,
      contextual: label,
      confidence: o.confidence,
      kind: o.kind,
      verify: 'pending',
      name: names[i],
      madeFrom,
      words,
      ...(o.placedBy === undefined || o.placedBy === 'human' ? { placedBy: 'human' as const } : {}),
    };
  });

  const now = Date.now();
  const obsoleted: Segment[] = inputs.map((s) => ({ ...s, obsoleteAt: now }));

  /* -- persist: obsolete in, new out, active leaves renumbered ------ */
  const inputIds = new Set(inputs.map((s) => s.id));
  const activeRest = (await getSegmentsByTask(taskId)).filter(
    (s) => isActiveLeaf(s) && !inputIds.has(s.id),
  );
  const leaves = [...activeRest, ...created].sort((a, b) => a.startSec - b.startSec);
  const revisions: { segment: Segment; clip?: Blob }[] = obsoleted.map((s) => ({ segment: s }));
  const createdIds = new Set(created.map((c) => c.id));
  leaves.forEach((s, i) => {
    if (createdIds.has(s.id)) {
      s.index = i;
      revisions.push({ segment: s, clip: sliceToWav(buffer, s.startSec, s.endSec) });
    } else if (s.index !== i) {
      revisions.push({ segment: { ...s, index: i } });
    }
  });
  await reviseSegments(revisions, [], obsoleted.map((s) => s.id));
  const status = await reconcileTask(taskId);
  return { created, obsoleted, status };
}
